import {
  ContextualMenu,
  IContextualMenuItem,
  mergeStyles,
} from "@fluentui/react";
import { toast } from "components/FluentToast";

import { isExternalOrReadonlyAdmin } from "form/util";
import { useTransferTempVehicle } from "hooks/data/mutations/useTransferTempVehicle";
import {
  getPolicyDataLines,
  isSetPolicyNumberAndDocumentDateHidden,
} from "pages/fleet/setPolicyNumberAndDocumentDateUtils";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { UserRole } from "types/types";
import { routes } from "../../../../config/routes";
import {
  setDeleteDialog,
  setDuplicateVehicleWithPremiumsDialog,
  setFlottomatDialog,
  setPolicyNumberAndDocumentDateDialog,
  setRemoveTEMPVehicle,
} from "../../../../store/fleetPage";
import { useSelector } from "../../../../store/hooks";
import { AppDispatch } from "../../../../store/store";

type Props = {
  vehicle: any;
  fleetId: number;
  onClickChangeStatus: () => void;
  onClickChangeIssuingStatus: () => void;
  onClickChangePendingIssuingStatus: () => void;
  // onClickCorrectStatusDate: () => void;
  onClickApproveStatus: () => void;
  onClickCancelStatus: () => void;
  isUpdateVehicleStatusLoading: boolean;
  isApproveVehicleStatusLoading: boolean;
  target: any;
  onDismiss: any;
};

const displayNoneClass = mergeStyles({
  display: "none",
});

const ContextualMenuItem = ({
  vehicle,
  fleetId,
  onDismiss,
  target,
  onClickChangeStatus,
  // onClickCorrectStatusDate,
  onClickChangeIssuingStatus,
  onClickChangePendingIssuingStatus,
  onClickApproveStatus,
  onClickCancelStatus,
  isUpdateVehicleStatusLoading,
  isApproveVehicleStatusLoading,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const taxonomy = useSelector((s) => s.taxonomy);
  const appSettings = useSelector((s) => s.fleetForm.fleetAppSettings);

  const { mutate: onTransferTempVehicle } = useTransferTempVehicle();

  const userRole = useSelector((s) => s.auth.userRole);
  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);

  const selectedVehicleIds = useSelector((s) => s.fleetPage.selectedVehicleIds);
  // VehicleContextMenu forwards the whole selection, so `vehicle` is an array
  // when more than one row is selected - normalize before reading row fields.
  const selectedVehicles: any[] = Array.isArray(vehicle) ? vehicle : [vehicle];
  const firstVehicle = selectedVehicles[0];
  const isSingleSelection = selectedVehicles.length === 1;
  const isTEMPSelection =
    selectedVehicles.length > 0 && selectedVehicles.every((v) => v?.isTEMP);
  const isMultipleAdminSelection =
    selectedVehicleIds.length > 1 && userRole !== UserRole.external;
  const isMultipleExternalSelection =
    selectedVehicleIds.length > 1 && userRole === UserRole.external;
  // Row-level status actions only make sense for a single row
  const vehicleStatusTaxCode = isSingleSelection
    ? taxonomy.VehicleStatusCode.byId[firstVehicle?.vehicleStatusCode]?.code
    : undefined;

  const shouldDisableCorrectStatusDate =
    new Date(vehicle.vehicleLastStatusDate) <
      new Date(vehicle.fleetPolicyCoverPeriodStartDate) ||
    new Date(vehicle.vehicleLastStatusDate) >=
      new Date(vehicle.fleetPolicyCoverPeriodEndDate);

  let items: IContextualMenuItem[] = [
    {
      key: "open",
      text: t("bfm.openInNewTab.label"),
      onClick: () => {
        window.open(
          routes[
            isTEMPSelection ? "editTempVehicle" : "readOnlyVehicle"
          ].getPath(firstVehicle?.fleetId, firstVehicle?.vehicleId),
        );
      },
      disabled: isUpdateVehicleStatusLoading || isMultipleAdminSelection,
    },
    {
      key: "edit",
      text: t("bfm.editVehicle.label"),
      onClick: () => {
        if (isTEMPSelection) {
          navigate(
            routes.editTempVehicle.getPath(
              firstVehicle.fleetId,
              firstVehicle.vehicleId,
            ),
          );
        }
        //
        else {
          navigate(
            routes.editVehicle.getPath(
              firstVehicle.fleetId,
              firstVehicle.vehicleId,
            ),
          );
        }
      },
      disabled:
        userRole === UserRole.adminreadonly ||
        isUpdateVehicleStatusLoading ||
        isMultipleAdminSelection ||
        isMultipleExternalSelection,
    },
    {
      key: "duplicate",
      text: t("bfm.duplicateVehicle.label"),
      onClick: () => {
        navigate(
          routes.duplicateVehicle.getPath(
            firstVehicle.fleetId,
            firstVehicle.vehicleId,
          ),
        );
      },
      className: isTEMPSelection ? displayNoneClass : "",
      disabled:
        userRole === UserRole.adminreadonly ||
        isUpdateVehicleStatusLoading ||
        isMultipleAdminSelection ||
        isMultipleExternalSelection,
    },
    {
      key: "duplicateWithPremiums",
      text: t("bfm.duplicateVehicleWithPremiums.label"),
      onClick: () => {
        dispatch(
          setDuplicateVehicleWithPremiumsDialog({
            isOpen: true,
            vehicleId: firstVehicle.vehicleId,
            fleetId: "" + fleetId,
            isTradeLicensePlate: firstVehicle.isTradeLicensePlate,
          }),
        );
      },
      className:
        isTEMPSelection ||
        isExternalOrReadonlyAdmin(userRole) ||
        !appSettings?.HAS_DUPLICATE_WITH_PREMIUMS_OPTION
          ? displayNoneClass
          : "",
      disabled:
        isUpdateVehicleStatusLoading ||
        isMultipleAdminSelection ||
        isMultipleExternalSelection,
    },
    {
      key: "changeStatus",
      text: t("bfm.changeStatus.label"),
      onClick: onClickChangeStatus,
      className: isTEMPSelection
        ? mergeStyles({
            display: "none",
          })
        : "",
      disabled:
        (userRole === UserRole.external &&
          [
            "VehicleStatusCode.PENDING_FOR_ACTIVATION",
            "VehicleStatusCode.PENDING_FOR_SUSPENSION",
            "VehicleStatusCode.PENDING_FOR_DEACTIVATION",
          ].includes(vehicleStatusTaxCode)) ||
        userRole === UserRole.adminreadonly ||
        isUpdateVehicleStatusLoading,
    },
  ];
  if (fleetAppSettings?.INCLUDE_ISSUING_STATUS) {
    items.push(
      {
        key: "changeIssuingStatus",
        text: t("bfm.changeIssuingStatus.label"),
        onClick: onClickChangeIssuingStatus,
        className:
          isTEMPSelection || isExternalOrReadonlyAdmin(userRole)
            ? displayNoneClass
            : "",
        disabled: isUpdateVehicleStatusLoading,
      },
      {
        key: "transfer",
        text: t("bfm.transferVehicle.label"),
        onClick: (e) => {
          e.preventDefault();
          onTransferTempVehicle(
            {
              fleetId: firstVehicle.fleetId,
              vehicleId: firstVehicle.vehicleId,
            },

            {
              onSuccess: (res) => {
                dispatch(setRemoveTEMPVehicle(res.data));
                toast.success(t("bfm.notification.transfer.success"));
                onDismiss();
              },
            },
          );
        },
        disabled:
          isExternalOrReadonlyAdmin(userRole) || isMultipleAdminSelection,
        className: !isTEMPSelection ? displayNoneClass : "",
      },
      {
        key: "delete",
        text: t("bfm.deleteTempVehicle.label"),
        className: isExternalOrReadonlyAdmin(userRole) ? displayNoneClass : "",
        disabled: isExternalOrReadonlyAdmin(userRole),
        onClick: () => {
          if (isTEMPSelection) {
            dispatch(
              setDeleteDialog({
                isOpen: true,
                fleetId: selectedVehicles[0]?.fleetId,
                vehicleIds: selectedVehicles.map((v) => Number(v.vehicleId)),
                isTEMP: true,
              }),
            );
          }
          //
          else {
            dispatch(
              setDeleteDialog({
                isOpen: true,
                vehicleIds: selectedVehicleIds,
                fleetId: "" + fleetId,
              }),
            );
          }
        },
      },
      {
        key: "approveStatus",
        text: t("bfm.approveStatus"),
        onClick: onClickApproveStatus,
        disabled: isApproveVehicleStatusLoading,
        className:
          isExternalOrReadonlyAdmin(userRole) ||
          isTEMPSelection ||
          ![
            "VehicleStatusCode.PENDING_FOR_ACTIVATION",
            "VehicleStatusCode.PENDING_FOR_SUSPENSION",
            "VehicleStatusCode.PENDING_FOR_DEACTIVATION",
          ].includes(vehicleStatusTaxCode)
            ? displayNoneClass
            : "",
      },
      {
        key: "cancelStatus",
        text: t("bfm.cancelStatus"),
        onClick: onClickCancelStatus,
        disabled:
          isApproveVehicleStatusLoading ||
          isMultipleAdminSelection ||
          userRole === UserRole.adminreadonly,
        className:
          isTEMPSelection ||
          ![
            "VehicleStatusCode.PENDING_FOR_ACTIVATION",
            "VehicleStatusCode.PENDING_FOR_SUSPENSION",
            "VehicleStatusCode.PENDING_FOR_DEACTIVATION",
          ].includes(vehicleStatusTaxCode)
            ? displayNoneClass
            : "",
      },
    );
  }

  items.push({
    key: "setPolicyNumberAndDocumentDate",
    text: t("bfm.setPolicyNumberAndDocumentDate.label"),
    onClick: () => {
      dispatch(
        setPolicyNumberAndDocumentDateDialog({
          isOpen: true,
          fleetId: "" + fleetId,
          vehicle: firstVehicle,
        }),
      );
    },
    className: isSetPolicyNumberAndDocumentDateHidden(
      fleetAppSettings,
      userRole,
      isTEMPSelection,
    )
      ? displayNoneClass
      : "",
    disabled:
      !isSingleSelection ||
      isMultipleAdminSelection ||
      getPolicyDataLines(fleetAppSettings, firstVehicle).length === 0,
  });

  items.push({
    key: "flotomat",
    text: t("bfm.generaliApplicationReport.label"),
    onClick: () => {
      dispatch(
        setFlottomatDialog({
          isOpen: true,
          fleetId: firstVehicle.fleetId,
          vehicleId: firstVehicle.vehicleId,
        }),
      );
    },
    className:
      isTEMPSelection ||
      !isSingleSelection ||
      isExternalOrReadonlyAdmin(userRole) ||
      (firstVehicle?.hullInsurerPartnerNumber !== "109925" &&
        firstVehicle?.mtplInsurerPartnerNumber !== "109925")
        ? displayNoneClass
        : "",
  });

  return (
    <ContextualMenu
      items={items}
      target={target}
      gapSpace={10}
      onDismiss={onDismiss}
    />
  );
};

export default ContextualMenuItem;
